import { clearCcoDatabaseContents, deleteCcoDatabases } from '@core/user-tenant/tenant-db-cleanup';

/**
 * Web lock held (shared) by every running app tab for the lifetime of its
 * page. The `/reset-local-data` path requests it exclusively: the browser
 * grants that only once every app tab has closed (locks auto-release on tab
 * close or crash), which is the reset's signal that no IndexedDB connections
 * remain and no ngrx-store-idb master tab is left to re-persist stale state
 * over the freshly wiped databases.
 */
export const APP_TAB_LOCK = 'cco-app-tab';

function webLocksSupported(): boolean {
  return typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks != null;
}

/**
 * Acquires the shared app-tab lock and holds it until the page is destroyed.
 * Resolves once the lock is GRANTED, so callers can defer opening IndexedDB
 * until a pending exclusive reset (if any) has finished — but no longer than
 * `timeoutMs`: shared requests queue BEHIND a pending exclusive one, so a
 * reset tab waiting for other tabs to close would otherwise hang every newly
 * opened tab on the splash screen indefinitely. A tab that times out boots
 * without the lock (the request stays queued and takes hold when granted);
 * the reset copes — its database deletion is blocked by the tab's open
 * connections and falls back to clearing the object stores. Resolves
 * immediately when Web Locks are unavailable, where the reset likewise
 * degrades to clearing, see performLocalDataReset.
 */
export function holdAppTabLock(timeoutMs = 3000): Promise<void> {
  if (!webLocksSupported()) {
    return Promise.resolve();
  }

  return new Promise<void>((proceed) => {
    const timer = setTimeout(proceed, timeoutMs);
    navigator.locks
      .request(APP_TAB_LOCK, { mode: 'shared' }, () => {
        clearTimeout(timer);
        proceed();
        // Never resolve: the lock is held until the document is destroyed.
        return new Promise<never>(() => undefined);
      })
      // AbortError/SecurityError (e.g. the document is being torn down) —
      // don't wedge bootstrap on it.
      .catch(() => {
        clearTimeout(timer);
        proceed();
      });
  });
}

/** The number of app tabs currently holding the shared app-tab lock. */
async function countAppTabs(): Promise<number> {
  try {
    const { held } = await navigator.locks.query();
    return (held ?? []).filter(lock => lock.name === APP_TAB_LOCK).length;
  } catch {
    return 0;
  }
}

/** Wipes sessionStorage and localStorage completely. */
export function clearAllWebStorage(): void {
  for (const storage of [ sessionStorage, localStorage ]) {
    try {
      storage.clear();
    } catch {
      // Storage unavailable — nothing to clear.
    }
  }
}

/**
 * Deletes every cco IndexedDB database outright, falling back to emptying
 * the object stores of any database whose deletion does not complete
 * (a blocked delete finishes on its own once the offending connection
 * closes; the interim clear guarantees the data is gone either way).
 */
async function wipeIndexedDb(tryDelete: boolean): Promise<void> {
  if (tryDelete) {
    const results = await deleteCcoDatabases(2000);
    if (results.every(result => result.outcome === 'deleted')) {
      return;
    }
  }
  await clearCcoDatabaseContents();
}

/**
 * What a `/reset-local-data` visit should reset:
 * - `data`: the cco IndexedDB databases plus sessionStorage and localStorage;
 * - `service-worker`: every service worker registration plus the Angular
 *   service worker's `ngsw:*` Cache Storage entries (the reboot after the
 *   reset registers a fresh worker from the network).
 */
export type LocalDataResetScope = 'data' | 'service-worker';

/**
 * Unregisters all service workers and deletes the Angular service worker's
 * caches. Best run while no other app tab exists: unregistration only fully
 * takes effect once every controlled client is gone, and any running app tab
 * would immediately re-register the worker.
 */
async function unregisterAllServiceWorkers(): Promise<void> {
  const registrations = await (navigator.serviceWorker?.getRegistrations() ?? Promise.resolve([]));
  await Promise.all(registrations.map(registration => registration.unregister().catch(() => false)));

  if (typeof caches !== 'undefined') {
    const names = await caches.keys();
    await Promise.all(names.filter(name => name.startsWith('ngsw:')).map(name => caches.delete(name)));
  }
}

/**
 * Wipes the local state selected by `scope` — see LocalDataResetScope.
 * Never rejects.
 *
 * With Web Locks available, waits for the exclusive app-tab lock first —
 * reporting the number of still-open app tabs via `reportOtherTabs` so the
 * caller can ask the user to close them — and then DELETES the databases
 * (safe: provably no connections). Without Web Locks, empties the databases'
 * object stores instead, since a delete could be blocked indefinitely by
 * another tab. The same exclusivity makes the service-worker scope reliable:
 * no controlled clients remain and no running tab re-registers the worker
 * mid-reset.
 *
 * MUST run before the Angular app bootstraps (see the `/reset-local-data`
 * branch in main.ts): a running app instance wins the single-tab
 * ngrx-store-idb master election, rehydrates the old `State` record into the
 * store, and persists it right back on the next action — resurrecting
 * everything this function just deleted (and, for the service-worker scope,
 * would re-register the worker).
 */
export async function performLocalDataReset(
  reportOtherTabs?: (openTabs: number) => void,
  scope: LocalDataResetScope = 'data',
): Promise<void> {
  const wipe = scope === 'service-worker'
    ? () => unregisterAllServiceWorkers()
    : async (tryDelete: boolean) => {
      await wipeIndexedDb(tryDelete);
      clearAllWebStorage();
    };

  try {
    if (!webLocksSupported()) {
      await wipe(false);
      return;
    }

    // Guards against a wedged lock queue: a pending request from a
    // back/forward-cached document is never granted but still blocks
    // everything queued behind it (observed in Chrome). When no tab holds
    // the lock for several consecutive polls yet our exclusive request is
    // still not granted, abort it and wipe anyway — the deletion falls back
    // to clearing if some hidden connection does turn out to exist.
    const zombieQueueGuard = new AbortController();
    let zeroHolderPolls = 0;
    const pollTimer = setInterval(() => void countAppTabs().then((count) => {
      reportOtherTabs?.(count);
      if (count > 0) {
        zeroHolderPolls = 0;
      } else if (++zeroHolderPolls >= 3) {
        zombieQueueGuard.abort();
      }
    }), 500);

    try {
      await navigator.locks.request(
        APP_TAB_LOCK,
        { mode: 'exclusive', signal: zombieQueueGuard.signal },
        async () => {
          clearInterval(pollTimer);
          reportOtherTabs?.(0);
          await wipe(true);
        },
      );
    } catch {
      // AbortError from the zombie-queue guard (or any other lock failure).
      reportOtherTabs?.(0);
      await wipe(true);
    } finally {
      clearInterval(pollTimer);
    }
  } catch {
    // Best-effort last resort (lock machinery failed): still try to wipe.
    try {
      await wipe(false);
    } catch {
      // The caller redirects into the app regardless.
    }
  }
}
